import { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import api, { apiErr, calendarCostSeconds } from "../../lib/api";
import { connectYoutubePopup } from "../../lib/youtubeOauth";
import { btnPrimary, CostBadge } from "../ui-bits";
import VoiceSelect from "../VoiceSelect";
import { toast } from "sonner";
import { X, Youtube, Loader2, Film, Sparkles, CheckCircle2, ArrowRight, ArrowLeft, Tv, Lightbulb, SlidersHorizontal, Mic, CalendarDays, Wand2 } from "lucide-react";

// Valores por defecto del calendario inicial (editables, ver estado calWeeks/calPerWeek más
// abajo) — mismas opciones que ya ofrece CalendarPanel.jsx para no sorprender con un rango
// distinto en dos sitios de la app.
const CAL_WEEKS = 4;
const CAL_VIDEOS_PER_WEEK = 3;
const CAL_PERWEEK_OPTIONS = [1, 2, 3, 4, 5, 6, 7];
const CAL_WEEKS_OPTIONS = [1, 2, 3, 4, 6, 8];
// Frases que rotan mientras se genera: la llamada real tarda —analiza vídeos de competencia con
// transcripciones antes de escribir— así que un loader mudo se siente colgado. Cada una nombra un
// paso REAL del proceso (ver /channels/{cid}/calendar/generate en el backend), no relleno vacío.
const CAL_PHRASE_KEYS = ["wiz.calPhrase1", "wiz.calPhrase2", "wiz.calPhrase3", "wiz.calPhrase4", "wiz.calPhrase5"];
const CAL_PHRASE_MS = 2200;

const inputCls = "w-full border-2 border-slate-300 rounded-lg px-4 py-3 text-lg bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-600/20 outline-none";
// Mismo catálogo que TabComposicion.jsx (composition.transforms) — aquí se fija el valor POR
// DEFECTO del canal (identidad visual, se decide una vez), no por vídeo. El 1-clic no pasa por
// TabComposicion en absoluto, así que sin esto "Retro" y el resto eran inalcanzables desde ahí.
const TRANSFORM_IDS = ["Espejo", "Color correction", "Crop 97%", "Film grain", "Retro"];
// Paquetes de tema — ver mismo comentario en ChannelModal.jsx. Claves 1:1 con LOWER_THIRD_STYLES.
const THEME_IDS = ["default", "dark", "color", "pill", "rule"];

export default function ChannelWizard({ config, onClose, onSaved }) {
  const { t, i18n } = useTranslation();
  const STEPS = t("wiz.steps", { returnObjects: true });
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [refChannels, setRefChannels] = useState("");
  const [voice, setVoice] = useState("");
  const [voiceName, setVoiceName] = useState("");
  const [language, setLanguage] = useState((i18n.resolvedLanguage || i18n.language || "es").startsWith("en") ? "en" : "es");
  // Transiciones desactivadas (2026-07): el backend fuerza corte directo en el render pase lo
  // que pase lo que se guarde aquí (ver TabComposicion.jsx), así que no se ofrece selector.
  const [transitions] = useState(["Corte directo"]);
  const [subtitles, setSubtitles] = useState(true);
  const [transforms, setTransforms] = useState([]);
  const [theme, setTheme] = useState("default");
  const toggleTransform = (id) =>
    setTransforms((tf) => (tf.includes(id) ? tf.filter((x) => x !== id) : [...tf, id]));
  const [busy, setBusy] = useState(false);
  // Estado del paso final "Calendario" — vive aparte de `busy` (que es el de crear el CANAL,
  // ya terminado cuando se llega aquí) porque son dos operaciones distintas con su propio spinner.
  const [savedChannel, setSavedChannel] = useState(null); // el canal ya existe una vez se llega aquí
  const [calBusy, setCalBusy] = useState(false);
  const [calDone, setCalDone] = useState(null); // null | {created, entries}
  const [phraseIdx, setPhraseIdx] = useState(0);
  // Editables por el usuario (antes fijos) — mismas opciones que ya ofrece CalendarPanel.jsx
  // para que el número no sorprenda: 1-7 vídeos/semana, semanas del mismo set 1/2/3/4/6/8.
  const [calPerWeek, setCalPerWeek] = useState(CAL_VIDEOS_PER_WEEK);
  const [calWeeks, setCalWeeks] = useState(CAL_WEEKS);
  const [oauthConnecting, setOauthConnecting] = useState(false);
  // Paso 1: se conecta ANTES de que el canal exista -- el token queda "pendiente" en el usuario
  // (ver /youtube/oauth/start con channel_id vacío) y create_channel() lo traslada al canal en
  // cuanto se crea (ver server.py). channelTitle, si llega, prellena el nombre en el paso 2.
  const [youtubeConnected, setYoutubeConnected] = useState(false);

  useEffect(() => {
    if (!calBusy) return;
    const id = setInterval(() => setPhraseIdx((i) => (i + 1) % CAL_PHRASE_KEYS.length), CAL_PHRASE_MS);
    return () => clearInterval(id);
  }, [calBusy]);

  // Una voz elegida para español no tiene por qué existir en el catálogo de ai33 filtrado por
  // inglés (y viceversa) — al cambiar el idioma del canal, se limpia la elección.
  useEffect(() => { setVoice(""); setVoiceName(""); }, [language]);

  const refList = refChannels.split("\n").map((x) => x.trim()).filter(Boolean);

  const next = () => {
    // El penúltimo paso ("Revisar") es el que de verdad crea el canal; el último ("Calendario")
    // solo puede empezar una vez el canal existe (el calendario cuelga de su channel_id), así
    // que necesita ser un paso aparte DESPUÉS de guardar, no la misma acción que "Siguiente".
    if (step === STEPS.length - 2) return save();
    if (step === 1 && !name.trim()) { toast.warning(t("wiz.warnNameStep")); return; }
    if (step === 3 && !description.trim()) { toast.warning(t("wiz.warnDesc")); return; }
    setStep((s) => s + 1);
  };

  const save = async () => {
    if (!name.trim()) { toast.warning(t("wiz.warnName")); setStep(1); return; }
    if (!description.trim()) { toast.warning(t("wiz.warnDesc")); setStep(3); return; }
    setBusy(true);
    const payload = {
      name: name.trim(), description, language, default_voice: voice, default_transitions: transitions,
      default_subtitles: subtitles, default_transforms: transforms, default_theme: theme,
      reference_channels: refList,
    };
    try {
      const { data } = await api.post("/channels", payload);
      toast.success(t("wiz.created"));
      setSavedChannel(data);
      setStep(STEPS.length - 1); // paso final: ofrecer el calendario, opcional
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  // Paso 1 (antes de crear el canal): channel_id vacío -> modo "pendiente" en el backend.
  const connectYoutubeStep0 = async () => {
    setOauthConnecting(true);
    try {
      const data = await connectYoutubePopup("");
      setYoutubeConnected(true);
      if (data.channel_title && !name.trim()) setName(data.channel_title);
      toast.success(data.channel_title ? t("wiz.connectedToast", { title: data.channel_title }) : t("wiz.connectedToastGeneric"));
      setStep(1);
    } catch (e) {
      if (e.message !== "popup_closed") toast.error(apiErr(e));
    } finally { setOauthConnecting(false); }
  };

  // Paso final (después de crear el canal): channel_id real -- para quien saltó el paso 1.
  const connectYoutube = async () => {
    if (!savedChannel) return;
    setOauthConnecting(true);
    try {
      const data = await connectYoutubePopup(savedChannel.id);
      setYoutubeConnected(true);
      toast.success(data.channel_title ? t("wiz.connectedToast", { title: data.channel_title }) : t("wiz.connectedToastGeneric"));
    } catch (e) {
      if (e.message !== "popup_closed") toast.error(apiErr(e));
    } finally { setOauthConnecting(false); }
  };

  // El canal YA existe llegados aquí (se creó en save()) — cerrar el wizard desde este punto,
  // se haya generado calendario o no, siempre debe refrescar la lista de canales del padre.
  // onClose() a secas (usado en los pasos anteriores) NO lo hace, por eso hace falta esta
  // función aparte en vez de reutilizar onClose para el botón de cerrar en este paso.
  const finish = () => onSaved(savedChannel);

  // Cerrar con la X o clicando fuera: si el canal YA se creó (llegamos al paso final), debe
  // recargar la lista igual que "Terminar" — onClose() a secas no lo hace, así que cerrar por
  // aquí en vez de por el botón dejaría el canal recién creado sin aparecer hasta recargar.
  const closeWizard = () => (savedChannel ? finish() : onClose());

  const generateCalendar = async () => {
    if (!savedChannel) return;
    setCalBusy(true);
    setPhraseIdx(0);
    try {
      const { data } = await api.post(`/channels/${savedChannel.id}/calendar/generate`, {
        weeks: calWeeks, videos_per_week: calPerWeek,
      });
      setCalDone(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setCalBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm overflow-y-auto vb-scroll" onClick={closeWizard}>
      <div className="min-h-full flex items-start sm:items-center justify-center sm:p-6 lg:p-10">
        <div onClick={(e) => e.stopPropagation()} data-testid="channel-wizard"
          className="relative bg-white w-full sm:max-w-xl md:max-w-2xl lg:max-w-3xl min-h-screen sm:min-h-0 sm:rounded-3xl shadow-2xl p-5 sm:p-8 lg:p-10">
          <button data-testid="wizard-close" onClick={closeWizard} className="absolute right-4 top-4 z-10 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full p-2 transition-colors"><X size={24} /></button>

          <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 pr-10 mb-6">{t("wiz.createChannel")}</h3>

          {/* Stepper */}
          <div className="flex items-center gap-1 mb-6">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-1 flex-1">
                <div className={`flex items-center gap-2 ${i <= step ? "text-blue-700" : "text-slate-300"}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${i < step ? "bg-blue-600 text-white" : i === step ? "bg-blue-100 text-blue-700 border-2 border-blue-600" : "bg-slate-100 text-slate-400"}`}>
                    {i < step ? <CheckCircle2 size={16} /> : i + 1}
                  </div>
                  <span className="text-sm font-semibold hidden sm:block">{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 ${i < step ? "bg-blue-600" : "bg-slate-200"}`} />}
              </div>
            ))}
          </div>

          {/* STEP 0: Conectar YouTube -- primero, para que si el usuario conecta, el nombre real
              del canal ya llegue prellenado al paso siguiente. Si no tiene canal (o no quiere
              conectar todavía), "Saltar" avanza igual y el nombre se escribe a mano. */}
          {step === 0 && (
            <div className="space-y-4" data-testid="wizard-step-youtube">
              <div className="flex items-start gap-3 bg-red-50 border-2 border-red-100 rounded-xl p-4">
                <Youtube size={22} className="text-red-600 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-700">{t("wiz.connectYtDesc")}</p>
              </div>
              {youtubeConnected ? (
                <div data-testid="wizard-youtube-connected" className="flex items-center gap-3 border-2 border-emerald-200 bg-emerald-50 rounded-xl p-4">
                  <CheckCircle2 size={22} className="text-emerald-600 shrink-0" />
                  <div>
                    <p className="text-base font-bold text-emerald-900">{t("wiz.connectedTitle")}</p>
                    {name.trim() && <p className="text-sm text-emerald-700">{name.trim()}</p>}
                  </div>
                </div>
              ) : (
                <button type="button" data-testid="wizard-connect-youtube" onClick={connectYoutubeStep0} disabled={oauthConnecting}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 min-h-[48px] px-5 text-base rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-60">
                  {oauthConnecting ? <Loader2 className="animate-spin" size={20} /> : <Youtube size={20} />} {t("wiz.connect")}
                </button>
              )}
            </div>
          )}

          {/* STEP 1: Nombre + Competencia */}
          {step === 1 && (
            <div className="space-y-4" data-testid="wizard-step-competencia">
              <div>
                <label className="block text-base font-semibold text-slate-800 mb-2">{t("wiz.nameLabel")}</label>
                <input data-testid="wizard-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("wiz.namePlaceholder")} className={inputCls} />
                {youtubeConnected ? (
                  <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mt-2">{t("wiz.nameFromYt")}</p>
                ) : (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">{t("wiz.nameExactWarning")}</p>
                )}
              </div>
              <div className="flex items-start gap-3 bg-amber-50 border-2 border-amber-100 rounded-xl p-4">
                <Lightbulb size={22} className="text-amber-500 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-700"><Trans i18nKey="wiz.compInfo" components={{ 0: <b />, 1: <b />, 2: <b /> }} /></p>
              </div>
              <div>
                <label className="block text-base font-semibold text-slate-800 mb-2 flex items-center gap-2"><Sparkles size={18} className="text-amber-500" /> {t("wiz.compLabel")} <span className="text-slate-400 font-normal">{t("wiz.compNote")}</span></label>
                <textarea data-testid="wizard-reference-channels" value={refChannels} onChange={(e) => setRefChannels(e.target.value)} rows={4} placeholder={"https://youtube.com/@CanalCompetencia1\nhttps://youtube.com/@CanalCompetencia2"} className={inputCls} />
              </div>
            </div>
          )}

          {/* STEP 2: Voz — paso propio (no un <select> más dentro de Ajustes): es la mitad de
              la experiencia del vídeo, así que aquí se puede escuchar una muestra de cada voz
              antes de decidir (VoiceSelect, el catálogo de ai33 — mismo componente que usa
              TabNarracion.jsx). Va ANTES que Ajustes a propósito: es la decisión más
              importante del canal. */}
          {step === 2 && (
            <div className="space-y-4" data-testid="wizard-step-voz">
              <div className="flex items-start gap-3 bg-violet-50 border-2 border-violet-100 rounded-xl p-4">
                <Mic size={22} className="text-violet-600 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-700">{t("wiz.voiceInfo")}</p>
              </div>
              <VoiceSelect language={language} value={voice} onChange={(v) => { setVoice(v.id); setVoiceName(v.name); }} />
              <button type="button" data-testid="wizard-voice-later" onClick={() => { setVoice(""); setVoiceName(""); }}
                className="text-sm text-slate-500 hover:text-slate-700 font-semibold underline underline-offset-2">
                {t("wiz.chooseAtCreate")}
              </button>
            </div>
          )}

          {/* STEP 3: Ajustes */}
          {step === 3 && (
            <div className="space-y-4" data-testid="wizard-step-ajustes">
              <div className="flex items-start gap-3 bg-slate-50 border-2 border-slate-100 rounded-xl p-4">
                <SlidersHorizontal size={22} className="text-blue-600 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-700"><Trans i18nKey="wiz.settingsInfo" components={{ 0: <b /> }} /></p>
              </div>
              <div>
                <label className="block text-base font-semibold text-slate-800 mb-2">{t("wiz.descLabel")}</label>
                <textarea data-testid="wizard-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder={t("wiz.descPlaceholder")} className={inputCls} />
                <p className="text-sm text-slate-500 mt-2">{t("wiz.descHint")}</p>
              </div>
              <div>
                <label className="block text-base font-semibold text-slate-800 mb-2">{t("wiz.languageLabel")}</label>
                <div className="grid grid-cols-2 gap-2" data-testid="wizard-language">
                  {[{ id: "es", label: "Español", flag: "https://flagcdn.com/es.svg" }, { id: "en", label: "English", flag: "https://flagcdn.com/gb.svg" }].map((l) => (
                    <button key={l.id} type="button" data-testid={`wizard-lang-${l.id}`} onClick={() => setLanguage(l.id)}
                      className={`flex items-center justify-center gap-2 border-2 rounded-lg px-3 py-2.5 text-base font-medium transition-colors ${language === l.id ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 hover:border-slate-300 text-slate-700"}`}>
                      <img src={l.flag} alt="" className="w-6 h-auto rounded-sm shadow-sm" /> {l.label}
                    </button>
                  ))}
                </div>
                <p className="text-sm text-slate-500 mt-2">{t("wiz.languageHint")}</p>
              </div>
              <label className="flex items-center gap-3 border-2 border-slate-200 rounded-lg px-4 py-3 cursor-pointer">
                <input type="checkbox" data-testid="wizard-subtitles" checked={subtitles} onChange={(e) => setSubtitles(e.target.checked)} className="w-6 h-6 accent-blue-600" />
                <span className="text-base font-semibold text-slate-800">{t("wiz.subtitlesDefault")}</span>
              </label>
              <div>
                <label className="block text-base font-semibold text-slate-800 mb-2">{t("chm.themeLabel")}</label>
                <div className="space-y-2" data-testid="wizard-theme">
                  {THEME_IDS.map((id) => (
                    <label key={id} className={`flex items-start gap-3 border-2 rounded-lg px-4 py-3 cursor-pointer transition-colors ${theme === id ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                      <input type="radio" data-testid={`wizard-theme-${id}`} name="wizard-theme" checked={theme === id} onChange={() => setTheme(id)} className="w-5 h-5 accent-blue-600 shrink-0 mt-0.5" />
                      <span>
                        <span className="block text-base font-medium text-slate-800">{t(`chm.theme.${id}.name`)}</span>
                        <span className="block text-sm text-slate-500 mt-0.5">{t(`chm.theme.${id}.desc`)}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-base font-semibold text-slate-800 mb-2">{t("comp.transformsTitle")}</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="wizard-transforms">
                  {TRANSFORM_IDS.map((id) => (
                    <label key={id} className={`flex items-center gap-3 border-2 rounded-lg px-4 py-3 cursor-pointer transition-colors ${transforms.includes(id) ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                      <input type="checkbox" data-testid={`wizard-transform-${id}`} checked={transforms.includes(id)} onChange={() => toggleTransform(id)} className="w-5 h-5 accent-blue-600" />
                      <span className="text-base font-medium text-slate-800">{t(`comp.transform.${id}.name`)}</span>
                      <span className="text-sm text-slate-500 ml-auto">{t(`comp.transform.${id}.desc`)}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: Revisar */}
          {step === 4 && (
            <div className="space-y-3" data-testid="wizard-step-revisar">
              <p className="text-base text-slate-600 mb-2">{t("wiz.reviewIntro")}</p>
              <Row icon={Tv} label={t("wiz.rowName")} value={name || "—"} />
              <Row icon={Youtube} label={t("wiz.rowYoutube")} value={youtubeConnected ? t("wiz.connectedTitle") : t("wiz.notConnected")} />
              <Row icon={Lightbulb} label={t("wiz.rowLanguage")} value={language === "en" ? "English" : "Español"} />
              <Row icon={Sparkles} label={t("wiz.rowComp")} value={refList.length ? t("wiz.channelsCount", { count: refList.length }) : t("wiz.none")} />
              <Row icon={Mic} label={t("wiz.rowVoice")} value={voice ? (voiceName || voice) : t("wiz.chooseAtCreate")} />
              <Row icon={SlidersHorizontal} label={t("wiz.rowSubtitles")} value={subtitles ? t("wiz.subtitlesOn") : t("wiz.subtitlesOff")} />
              <Row icon={Film} label={t("wiz.rowTransforms")} value={transforms.length ? transforms.map((id) => t(`comp.transform.${id}.name`)).join(" · ") : t("wiz.none")} />
              <Row icon={Sparkles} label={t("wiz.rowTheme")} value={t(`chm.theme.${theme}.name`)} />
              {refList.length === 0 && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">{t("wiz.noCompNote")}</p>}
            </div>
          )}

          {/* STEP 5: Calendario — solo alcanzable DESPUÉS de guardar (savedChannel ya existe).
              Si no se conectó YouTube en el paso 1, aquí se ofrece una segunda oportunidad (ya
              con channel_id real). Si ya se conectó, no hace falta repetirlo. Sin canales de
              referencia el backend rechazaría la generación del calendario (misma exigencia que
              /recommendations, ver _require_competitor_context), así que aquí se avisa en vez de
              ofrecer un botón que fallaría siempre. */}
          {step === 5 && (
            <div className="space-y-4" data-testid="wizard-step-calendario">
              {!youtubeConnected && (
              <div className="flex items-center justify-between gap-3 border-2 border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-3 min-w-0">
                  <Youtube size={22} className="text-red-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-base font-bold text-slate-900">{t("wiz.connectYtLabel")}</p>
                    <p className="text-sm text-slate-500">{t("wiz.connectYtDesc")}</p>
                  </div>
                </div>
                <button type="button" data-testid="wizard-connect-youtube" onClick={connectYoutube} disabled={oauthConnecting}
                  className="shrink-0 inline-flex items-center gap-2 min-h-[44px] px-4 text-sm rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-60">
                  {oauthConnecting ? <Loader2 className="animate-spin" size={18} /> : <Youtube size={18} />} {t("wiz.connect")}
                </button>
              </div>
              )}
              {refList.length === 0 ? (
                <div className="flex items-start gap-3 bg-amber-50 border-2 border-amber-100 rounded-xl p-4">
                  <CalendarDays size={22} className="text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-base font-bold text-slate-800">{t("wiz.calNoCompTitle")}</p>
                    <p className="text-sm text-slate-700 mt-1">{t("wiz.calNoCompDesc")}</p>
                    <button type="button" data-testid="wizard-cal-back" onClick={() => setStep(1)}
                      className="mt-3 text-sm font-semibold text-blue-700 hover:text-blue-900 underline underline-offset-2">
                      {t("wiz.calBack")}
                    </button>
                  </div>
                </div>
              ) : calDone ? (
                <div data-testid="wizard-cal-done" className="flex items-start gap-3 bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
                  <CheckCircle2 size={22} className="text-emerald-600 mt-0.5 shrink-0" />
                  <p className="text-base font-semibold text-emerald-900">
                    {t("wiz.calDone", { count: calDone.created, weeks: calWeeks })}
                  </p>
                </div>
              ) : calBusy ? (
                <div data-testid="wizard-cal-generating" className="flex flex-col items-center justify-center gap-4 py-10 text-center">
                  <div className="relative">
                    <Wand2 size={40} className="text-blue-600 animate-pulse" />
                  </div>
                  <p className="text-lg font-bold text-slate-900">{t("wiz.calGenerating")}</p>
                  {/* La frase cambia de key en cada rotación para que Reveal-less CSS transition
                      (fade simple) se reinicie — sin key repetida, React reutiliza el mismo nodo
                      y el texto salta sin transición. */}
                  <p key={phraseIdx} className="text-sm text-slate-500 animate-fade-in">
                    {t(CAL_PHRASE_KEYS[phraseIdx])}
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-3 bg-blue-50 border-2 border-blue-100 rounded-xl p-4">
                    <CalendarDays size={22} className="text-blue-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-slate-700">
                      {t("wiz.calIntro", { n: calPerWeek * calWeeks, weeks: calWeeks })}
                    </p>
                  </div>
                  {/* Mismas opciones que CalendarPanel.jsx (perWeek 1-7, weeks 1/2/3/4/6/8) —
                      antes venían fijas a 3x4, ahora el usuario decide como en el resto de la app. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("calp.videosPerWeek")}</label>
                      <div className="flex flex-wrap gap-1.5">
                        {CAL_PERWEEK_OPTIONS.map((n) => (
                          <button key={n} type="button" data-testid={`wizard-cal-perweek-${n}`} onClick={() => setCalPerWeek(n)}
                            className={`w-10 h-10 rounded-lg font-bold ${calPerWeek === n ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("calp.weeks")}</label>
                      <select data-testid="wizard-cal-weeks" value={calWeeks} onChange={(e) => setCalWeeks(parseInt(e.target.value, 10))}
                        className="w-full border-2 border-slate-300 rounded-lg px-3 py-2.5 text-base outline-none focus:border-blue-600">
                        {CAL_WEEKS_OPTIONS.map((w) => <option key={w} value={w}>{w > 1 ? t("calp.weeksN", { n: w }) : t("calp.weekN", { n: w })}</option>)}
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Nav buttons */}
          {/* El paso final "Calendario" tiene su propia barra de botones (más abajo): el canal
              ya existe llegados aquí, así que "Atrás" no tiene sentido (no se puede des-crear) y
              "Siguiente"/"Crear canal" no aplica — la acción es Generar/Omitir/Terminar. */}
          {step !== STEPS.length - 1 && (
            <div className="flex items-center justify-between gap-3 mt-7">
              {step > 0 ? (
                <button data-testid="wizard-back" onClick={() => setStep((s) => s - 1)} disabled={busy} className="min-h-[48px] px-4 sm:px-5 text-base rounded-xl border-2 border-slate-300 text-slate-700 font-bold hover:border-slate-400 flex items-center justify-center gap-2"><ArrowLeft size={18} /> {t("wiz.back")}</button>
              ) : <span />}
              <div className="flex items-center gap-2">
                <button data-testid="wizard-next" onClick={next} disabled={busy} className={step === 0 && !youtubeConnected ? "px-4 py-3 text-slate-500 font-semibold hover:text-slate-700" : btnPrimary}>
                  {busy ? <Loader2 className="animate-spin" size={20} /> : null}
                  {step === STEPS.length - 2 ? (busy ? t("wiz.creating") : t("wiz.createBtn"))
                    : step === 0 && !youtubeConnected ? t("wiz.skipConnect") : t("wiz.next")}
                  {step < STEPS.length - 2 && step !== 0 && <ArrowRight size={18} />}
                </button>
              </div>
            </div>
          )}

          {step === STEPS.length - 1 && (
            <div className="flex items-center justify-end gap-2 mt-7">
              {refList.length === 0 ? (
                <button data-testid="wizard-cal-finish" onClick={finish} className={btnPrimary}>
                  {t("wiz.calFinish")} <ArrowRight size={18} />
                </button>
              ) : calDone ? (
                <button data-testid="wizard-cal-finish" onClick={finish} className={btnPrimary}>
                  {t("wiz.calFinish")} <ArrowRight size={18} />
                </button>
              ) : (
                <>
                  {!calBusy && (
                    <button data-testid="wizard-cal-skip" onClick={finish} className="px-4 py-3 text-slate-500 font-semibold hover:text-slate-700">
                      {t("wiz.calSkip")}
                    </button>
                  )}
                  <button data-testid="wizard-cal-generate" onClick={generateCalendar} disabled={calBusy} className={btnPrimary}>
                    {calBusy ? <Loader2 className="animate-spin" size={20} /> : <Wand2 size={20} />}
                    {t("wiz.calGenerate")}
                    {!calBusy && <CostBadge seconds={calendarCostSeconds(calPerWeek * calWeeks)} tip={t("calp.genTip")} />}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 border-2 border-slate-200 rounded-lg px-4 py-3">
      <Icon size={20} className="text-blue-600 shrink-0" />
      <span className="text-sm font-semibold text-slate-500 w-28 shrink-0">{label}</span>
      <span className="text-base text-slate-900 truncate">{value}</span>
    </div>
  );
}
